import { Transforms, Path, Editor, Node } from 'slate';
import { ReactEditor } from 'slate-react';

import HelptapEditor from '../';

import * as baseNodeUtilities from './base-node';
import elementTypes from '../../elements/types';

export function getDefaultOptions() {

  const columnsCount = 3;
  const options = {
    ...baseNodeUtilities.getDefaultOptions(),
    children: [buildRowNode(columnsCount, '')],
    id: `table-${Date.now()}`,
    properties: {
      columnsCount
    }
  };

  return options;

}

export function buildCellNode(cell = {}, text = '') {

  const node = {
    padding: [5, 5, 5, 5],
    width: '200px',
    ...cell,
    alignment: 'left',
    type: elementTypes.TABLE_CELL,
    children: [{ text }]
  };

  return node;

}

export function buildRowNode(cellsCount, rowNumber = '', cell = {}) {

  const rowNode = {
    type: elementTypes.TABLE_ROW,
    children: []
  };

  for (let index = 0; index < cellsCount; index++) {
    rowNode.children.push(buildCellNode(cell, rowNumber));
  }

  return rowNode;

}

export function buildNode() {

  const node = {
    ...getDefaultOptions(),
    type: elementTypes.TABLE
  };

  return node;

}

function select(editor, tablePath) {

  Transforms.select(editor, [...tablePath, 0, 0]);
  ReactEditor.focus(editor);

}

function doInsert(editor, path) {

  const nodeOptions = buildNode();
  Transforms.insertNodes(editor, nodeOptions, { at: path });
  select(editor, path);
}

export function insert(editor, options, editorOptions, targetNodePath, nextNodePath) {

  Editor.withoutNormalizing(editor, () => {
    doInsert(editor, nextNodePath);
  });

}

export function toggle(editor, options, editorOptions, targetNodePath, nextNodePath) {

  Editor.withoutNormalizing(editor, () => {
    doInsert(editor, targetNodePath);
    Transforms.delete(editor, { at: Path.next(targetNodePath) });
  });

}

export function cloneNode(node) {

  return HelptapEditor.cloneNode(node);

}

export function duplicate(editor, node, nodePath) {

  const clonedNode = cloneNode(node);

  HelptapEditor.insertNode(editor, clonedNode, nodePath);

}

export function getCellsCount(editor) {

  const tablePath = Path.ancestors(editor.selection.anchor.path)[1];

  const [table] = Editor.node(editor, tablePath);

  return table.children[0].children.length;

}

export function getRowInsertPoint(editor) {

  return Path.next(Path.ancestors(editor.selection.anchor.path)[2]);

}

function isNodeEmpty(node) {

  return Node.string(node) === '';

}

function isLastRow(table, rowPath) {

  return (rowPath[rowPath.length - 1]) === (table.children.length - 1);

}

function copyStructure(row) {

  const copied = {
    ...row,
    children: []
  };
  const cells = row.children;

  for (let cell of cells) {

    copied.children.push({
      ...cell,
      children: [{ text: '' }]
    });

  }

  return copied;

}

export function determineNextNodeAndLocation(editor) {

  const ancestors = Path.ancestors(editor.selection.anchor.path);

  const tablePath = ancestors[1];
  const rowPath = ancestors[2];

  const [table] = Editor.node(editor, tablePath);
  const [row] = Editor.node(editor, rowPath);
  const [cell] = Editor.node(editor, ancestors[3]);

  if (isNodeEmpty(cell) === true && isNodeEmpty(row) === true) {

    Transforms.removeNodes(editor, { at: rowPath });

    if (isLastRow(table, rowPath) === false) {
      Transforms.splitNodes(editor, { at: rowPath });
      HelptapEditor.setAttributes(editor, { id: `table-${Date.now()}` }, Path.next(tablePath));
    }

    return [HelptapEditor.paragraph.buildNode(), { at: Path.next(tablePath) }];

  }

  const at = getRowInsertPoint(editor);

  return [copyStructure(row), { at }];

}

function isLastCell(table, cellPath) {

  return (table.children[0].children.length - 1 === cellPath[cellPath.length - 1]);

}

function getNextCellPath(table, ancestors) {

  const rowPath = ancestors[2];
  const cellPath = ancestors[3];

  if (isLastCell(table, cellPath) === true) {

    if (isLastRow(table, rowPath) === true) {

      return null;

    } else {

      return [...Path.next(rowPath), 0];

    }

  } else {

    return Path.next(cellPath);

  }

}

function getPreviousCellPath(table, ancestors) {

  const rowPath = ancestors[2];
  const cellPath = ancestors[3];

  if (cellPath[cellPath.length - 1] === 0) {

    if (rowPath[rowPath.length - 1] === 0) {
      return null;
    }

    return [...Path.previous(rowPath), table.children[0].children.length - 1];

  } else {
    return Path.previous(cellPath);
  }

}

export function focusNextCell(editor, table, ancestors) {

  let nextCellPath = getNextCellPath(table, ancestors);

  if (nextCellPath === null) {
    return false;
  }

  // Below three lines are not working!!

  Transforms.select(editor, nextCellPath);
  Transforms.collapse(editor, { edge: 'start' });

  ReactEditor.focus(editor);

  return true;

}

export function focusPreviousCell(editor, table, ancestors) {

  let previousCellPath = getPreviousCellPath(table, ancestors);

  if (previousCellPath === null) {
    return false;
  }

  // Below three lines are not working!!

  Transforms.select(editor, previousCellPath);
  Transforms.collapse(editor, { edge: 'end' });

  ReactEditor.focus(editor);

  return true;

}

function getTableRows(table, rowsCount) {

  const previousRows = table.children;
  const previousRowsCount = previousRows.length;
  const firstRow = previousRows[0];
  const totalColumns = firstRow.children.length;

  const firstCell = firstRow.children[0];

  function addColumns() {

    let columns = [];

    for (let index = 0; index < totalColumns; index++) {

      columns.push({
        ...firstCell,
        type: elementTypes.TABLE_CELL,
        children: [{ text: '' }]
      });
    }
    return columns;
  };

  const rows = [];
  const requiredNodes = rowsCount - previousRowsCount;

  for (let index = 0; index < requiredNodes; index++) {

    const children = addColumns();

    rows.push({
      type: elementTypes.TABLE_ROW,
      children: children
    });
  }

  return rows;

}

export function updateRows(editor, table, elementPath, newRowsCount) {

  const rowsCount = table.children.length;

  if (newRowsCount > rowsCount) {
    const nodes = getTableRows(table, newRowsCount);
    Transforms.insertNodes(editor, nodes, { at: [...elementPath, rowsCount] });
  } else {
    for (let index = rowsCount - 1; index >= newRowsCount; index--) {
      Transforms.removeNodes(editor, { at: [...elementPath, index] });
    }
  }

}

export function getTableColumns(table, newColumnsCount) {

  const firstRow = table.children[0].children;
  const columnsCount = firstRow.length;
  const firstCell = firstRow[0];

  const columns = [];

  const requiredNodes = newColumnsCount - columnsCount;

  for (let index = 0; index < requiredNodes; index++) {
    columns.push({
      ...firstCell,
      type: elementTypes.TABLE_CELL,
      children: [{ text: '' }]
    });
  }

  return columns;

}

export function updateColumns(editor, table, tablePath, newColumnsCount) {

  const rowsCount = table.children.length;
  const columnsCount = table.children[0].children.length;

  Editor.withoutNormalizing(editor, () => {

    HelptapEditor.setAttributes(editor, {
      properties: {
        ...(table.properties || {}),
        columnsCount: newColumnsCount
      },
      columnWidths: (new Array(newColumnsCount)).fill(100 / newColumnsCount)
    }, tablePath);

    try {

      if (newColumnsCount > columnsCount) {
        for (let index = 0; index < rowsCount; index++) {
          const nodes = getTableColumns(table, newColumnsCount);
          Transforms.insertNodes(editor, nodes, { at: [...tablePath, index, columnsCount] });
        }
      } else {

        for (let rowIndex = 0; rowIndex < rowsCount; rowIndex++) {

          for (let columnIndex = columnsCount - 1; columnIndex >= newColumnsCount; columnIndex--) {

            Transforms.removeNodes(editor, { at: [...tablePath, rowIndex, columnIndex] });

          }

        }

      }

    } catch (exception) {

      // When updating columns count from single digit(3,4,5) to double digit(10) fast, this happens!
      // However, the UI updated with correct number of columns
      console.log(exception);

    }

  });

}

function isFirstRow(rowPath) {

  return rowPath[rowPath.length - 1] === 0;

}

export function tryDeleteTableCell(editor) {

  const ancestors = Path.ancestors(editor.selection.anchor.path);

  const tablePath = ancestors[1];
  const rowPath = ancestors[2];
  const cellPath = ancestors[3];

  const [row] = Editor.node(editor, rowPath);

  if (cellPath[cellPath.length - 1] !== 0) {
    Transforms.select(editor, Path.previous(cellPath));
    ReactEditor.focus(editor);
    return;
  }

  if (isNodeEmpty(row) === false) {

    let nextPath = null;

    if (isFirstRow(rowPath) === true) {

      if (Path.equals(tablePath, [0]) === true) {
        return;
      }

      nextPath = Path.previous(tablePath);
    } else {
      nextPath = [...Path.previous(rowPath), row.children.length - 1];
    }
    Transforms.select(editor, nextPath);
    ReactEditor.focus(editor);
    return;
  }

  let [table] = Editor.node(editor, tablePath);

  if (table.children.length === 1) {
    Transforms.removeNodes(editor, { at: tablePath });
  } else {
    Transforms.removeNodes(editor, { at: rowPath });
  }

}

export function deleteNextNode(editor, tablePath, tableNode, nextNodePath, nextNode) {

  Transforms.removeNodes(editor, { at: nextNodePath });

}

export function setRowPadding(editor, padding, tablePath) {

  let [table] = Editor.node(editor, tablePath);
  const rowsCount = table.children.length;
  const columnsCount = table.children[0].children.length;

  for (let rowIndex = 0; rowIndex < rowsCount; rowIndex++) {

    const rowPath = [...tablePath, rowIndex];

    for (let columnIndex = 0; columnIndex < columnsCount; columnIndex++) {

      Transforms.setNodes(editor, { padding }, { at: [...rowPath, columnIndex] });

    }

  }

}

export function updateColumnWidth(editor, cellPath, cellWidth, cellElement) {

  const [tableIndex, , cellIndex] = cellPath;

  const [table] = Editor.node(editor, [tableIndex]);

  const rowsCount = table.children.length;

  for (let rowIndex = 0; rowIndex < rowsCount; rowIndex++) {

    HelptapEditor.setAttributes(editor, { width: cellWidth }, [tableIndex, rowIndex, cellIndex]);

  }

}

export function replaceRowForUser(editor, rowPath) {

  Editor.withoutNormalizing(editor, () => {

    Transforms.removeNodes(editor, { at: rowPath });
    HelptapEditor.paragraph.insert(editor, Path.next(Path.parent(rowPath)));

  });

}

export function insertRowForUser(editor, rowPath, columnsCount) {

  Editor.withoutNormalizing(editor, () => {

    const row = buildRowNode(columnsCount, '');

    Transforms.insertNodes(editor, row, { at: rowPath });

    Transforms.select(editor, [...rowPath, 0]);

  });

}

export function removeRow(editor, rowPath) {

  Transforms.removeNodes(editor, { at: rowPath });

}

export function gotoCellEnd(editor, cellPath) {

  Transforms.select(editor, cellPath);
  Transforms.collapse(editor, { edge: 'end' });

}